import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, {useState} from "react";
import {useAnalytics} from "../context/AnalyticsContext";
import {ComposePostEvents} from "../services/analytics/events";
import {TrackedButton} from "./TrackedButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import {Tooltip} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import styled from "@emotion/styled";
import {getPlatformIconJSX} from "../utils/platformUtils";

// Styled Components
const SelectedAccountsStack = styled(Stack)(({theme}) => ({
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const StyledAvatar = styled(Avatar)(({ theme, selected, isGrayscale }) => ({
    width: 40,
    height: 40,
    filter: isGrayscale ? 'grayscale(100%)' : 'none',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    ...(selected && {
        border: `3px solid ${theme.palette.success.main}`,
        boxSizing: 'border-box',
        transform: 'scale(1.05)',
    }),
    '&:hover': {
        transform: selected ? 'scale(1.05)' : 'scale(1.02)',
        filter: 'none',
    }
}));

const AvatarWrapper = styled.div`
    position: relative;
    display: inline-block;
    transition: all 0.2s ease-in-out;
`;

const IconOverlay = styled.div`
    position: absolute;
    bottom: -5px;
    right: -5px;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;


const AccountSelection = ({accounts, selectedAccounts, handleAccountChange}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [anchorEl, setAnchorEl] = useState(null);
    const {trackEvent} = useAnalytics();

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        trackEvent(ComposePostEvents.MOBILE_SELECT_SOCIAL_ACCOUNTS_BTN_CLICKED.name);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (account) => {
        handleAccountChange(account.id, account.username, account.platform, account.account_img);
        // Don't close menu immediately to allow multiple selections
    };

    const isSelected = (accountId) => {
        return selectedAccounts.some(acc => acc.accountId === accountId);
    };

    if (isMobile) {
        return (
            <>
                <TrackedButton
                    variant="outlined"
                    onClick={handleClick}
                    trackingData={ComposePostEvents.MOBILE_SELECT_SOCIAL_ACCOUNTS_BTN_CLICKED}
                    endIcon={<ExpandMoreIcon/>}
                    fullWidth
                    aria-label="Select social media accounts"
                >
                    Select Accounts ({selectedAccounts.length})
                </TrackedButton>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    PaperProps={{
                        style: {
                            maxHeight: '60vh',
                            width: '280px',
                        },
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    {accounts.map((account) => {
                        const selected = isSelected(account.id);
                        return (
                            <MenuItem
                                key={account.id}
                                onClick={() => handleMenuItemClick(account)}
                                selected={selected}
                                sx={{
                                    backgroundColor: selected ? 'action.selected' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: selected ? 'action.selected' : 'action.hover',
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <AvatarWrapper>
                                        <StyledAvatar
                                            alt={`${account.username} on ${account.platform}`}
                                            selected={selected}
                                            isGrayscale={!selected}
                                            src={account.account_img}
                                        />
                                        <IconOverlay>
                                            {getPlatformIconJSX(account.platform)}
                                        </IconOverlay>
                                    </AvatarWrapper>
                                </ListItemIcon>
                                <ListItemText
                                    primary={account.username}
                                    secondary={account.platform.charAt(0).toUpperCase() + account.platform.slice(1)}
                                    primaryTypographyProps={{
                                        fontWeight: selected ? 'medium' : 'normal'
                                    }}
                                />
                            </MenuItem>
                        );
                    })}
                    <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', mt: 1 }}>
                        <TrackedButton
                            variant="text"
                            size="small"
                            onClick={handleClose}
                            fullWidth
                            trackingData={ComposePostEvents.MOBILE_SELECT_ACCOUNTS_DONE}
                        >
                            Done
                        </TrackedButton>
                    </Box>
                </Menu>

                {selectedAccounts.length > 0 && (
                    <SelectedAccountsStack>
                        {selectedAccounts.map((account) => (
                            <Chip
                                key={account.accountId}
                                avatar={
                                    <AvatarWrapper>
                                        <StyledAvatar
                                            alt={`${account.username} on ${account.platform}`}
                                            src={account.account_img}
                                            selected={true}
                                            isGrayscale={false}
                                            sx={{ width: 24, height: 24 }}
                                        />
                                        <IconOverlay style={{transform: 'scale(0.7)'}}>
                                            {getPlatformIconJSX(account.platform)}
                                        </IconOverlay>
                                    </AvatarWrapper>
                                }
                                label={`${account.username} (${account.platform})`}
                                onDelete={() => handleAccountChange(account.accountId, account.username, account.platform, account.account_img)}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                    </SelectedAccountsStack>
                )}
            </>
        );
    }

    // Desktop view - horizontal layout with improved multi-select
    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Select Social Media Accounts ({selectedAccounts.length} selected)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {accounts.map((account) => {
                        const selected = isSelected(account.id);
                        return (
                            <Tooltip
                                key={account.id}
                                title={`${account.platform.toUpperCase()}: ${account.username}${selected ? ' (Selected)' : ''}`}
                                arrow
                            >
                                <IconButton
                                    onClick={() => handleAccountChange(account.id, account.username, account.platform, account.account_img)}
                                    size="large"
                                    sx={{
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            backgroundColor: 'action.hover',
                                        }
                                    }}
                                    aria-label={`${selected ? 'Deselect' : 'Select'} ${account.username} on ${account.platform}`}
                                >
                                    <AvatarWrapper>
                                        <StyledAvatar
                                            alt={`${account.username} on ${account.platform}`}
                                            selected={selected}
                                            isGrayscale={!selected}
                                            src={account.account_img}
                                        />
                                        <IconOverlay>
                                            {getPlatformIconJSX(account.platform)}
                                        </IconOverlay>
                                    </AvatarWrapper>
                                </IconButton>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};

export default AccountSelection;